import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Button,
  Input,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Separator,
} from "@lemonadetraining/web-ui";
import { IconBrandYoutube, IconCircleMinus } from "@tabler/icons-react";
import MyModal from "@lemonadetraining/web-shared/components/MyModal";
import getYouTubeID from "get-youtube-id";

const MAX_LINKS = 6;

const INVALID_LINK_MESSAGE =
  "This is not a YouTube link. Copy the link from YouTube — it looks like https://www.youtube.com/watch?v=… or https://youtu.be/…";

/** `getYouTubeID` only parses strings containing "youtube"/"youtu.be" — a bare ID returns null. */
export const youTubeWatchURL = (youTubeID: string) =>
  `https://www.youtube.com/watch?v=${youTubeID}`;

interface LinkRow {
  key: number;
  text: string;
  /** Stored ID this row was seeded with, cleared on edit so the text is re-parsed instead. */
  seededID: string | null;
}

interface AddYouTubeProps {
  youTubeIDs?: string[];
  onAddYouTubeLink?: (youTubeIDs: string[]) => void;
  readOnly?: boolean;
}

/** Animated row that smoothly expands on mount and collapses on removal. */
const AnimatedRow: React.FC<{
  children: React.ReactNode;
  onRemoved?: () => void;
  removing?: boolean;
}> = ({ children, removing, onRemoved }) => {
  const rowRef = useRef<HTMLDivElement>(null);

  // Expand on mount: start at 0 → measure scrollHeight → animate to it → clear to auto
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return;
    const height = el.scrollHeight;
    el.style.height = "0px";
    el.style.opacity = "0";
    // Force reflow
    void el.offsetHeight;
    el.style.height = `${height}px`;
    el.style.opacity = "1";
    const onEnd = () => {
      el.style.height = "auto";
    };
    el.addEventListener("transitionend", onEnd, { once: true });
    const fallback = setTimeout(onEnd, 300);
    return () => {
      el.removeEventListener("transitionend", onEnd);
      clearTimeout(fallback);
    };
  }, []);

  // Collapse on removal: lock current height → animate to 0 → call onRemoved
  useEffect(() => {
    if (!removing || !rowRef.current) return;
    const el = rowRef.current;
    el.style.height = `${el.scrollHeight}px`;
    void el.offsetHeight;
    el.style.height = "0px";
    el.style.opacity = "0";
    const onEnd = () => onRemoved?.();
    el.addEventListener("transitionend", onEnd, { once: true });
    const fallback = setTimeout(onEnd, 300);
    return () => {
      el.removeEventListener("transitionend", onEnd);
      clearTimeout(fallback);
    };
  }, [removing, onRemoved]);

  return (
    <div
      ref={rowRef}
      style={{ height: 0, opacity: 0, transition: "height 250ms ease, opacity 200ms ease" }}
      className="overflow-hidden"
    >
      {children}
    </div>
  );
};

const AddYouTube: React.FC<AddYouTubeProps> = ({ youTubeIDs, onAddYouTubeLink, readOnly }) => {
  const nextKeyRef = useRef(0);

  const seedRows = useCallback(
    (ids: string[]): LinkRow[] =>
      ids.map((id) => {
        // Cards saved before this dialog validated its input can hold a null
        // where a parse failed; show it as the empty row it always was.
        const seededID = typeof id === "string" && id !== "" ? id : null;
        return {
          key: nextKeyRef.current++,
          text: seededID ? youTubeWatchURL(seededID) : "",
          seededID,
        };
      }),
    []
  );

  const [showAddUrl, setShowAddUrl] = useState(false);
  const [rows, setRows] = useState<LinkRow[]>(() => seedRows(youTubeIDs || []));
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [removingIdx, setRemovingIdx] = useState<number | null>(null);

  // A row being animated out is already gone as far as the data is concerned:
  // what a save writes must never depend on the collapse callback having fired.
  const liveRows = removingIdx === null ? rows : rows.filter((_, i) => i !== removingIdx);

  const isAddNewLinkDisabled = liveRows.length >= MAX_LINKS;

  // Saving REPLACES the card's list, so the dialog must re-seed from the card on
  // every open: a stale list would destroy videos it never showed.
  const showModalHandler = () => {
    setRows(seedRows(youTubeIDs || []));
    setErrors({});
    setRemovingIdx(null);
    setShowAddUrl(true);
  };

  const hideModalHandler = () => {
    setShowAddUrl(false);
  };

  const onAddNewLinkField = () => {
    setRows((prev) => [...prev, { key: nextKeyRef.current++, text: "", seededID: null }]);
  };

  const onRemoveLinkField = useCallback((idx: number) => {
    setRemovingIdx(idx);
  }, []);

  const handleRemoved = useCallback(() => {
    if (removingIdx === null) return;
    const idx = removingIdx;
    setRemovingIdx(null);
    setRows((prev) => prev.filter((_, i) => i !== idx));
  }, [removingIdx]);

  const onRowTextChanged = (key: number, text: string) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, text, seededID: null } : row))
    );
    setErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const onAddYouTubeLinkClicked = () => {
    const nextErrors: Record<number, string> = {};
    const nextIDs: string[] = [];

    liveRows.forEach((row) => {
      if (row.seededID !== null) {
        nextIDs.push(row.seededID);
        return;
      }
      const text = row.text.trim();
      // A blank row never held a video, so dropping it loses nothing.
      if (text === "") return;
      const parsed = getYouTubeID(text);
      if (!parsed) {
        nextErrors[row.key] = INVALID_LINK_MESSAGE;
        return;
      }
      nextIDs.push(parsed);
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (onAddYouTubeLink) {
      onAddYouTubeLink(nextIDs);
    }
    hideModalHandler();
  };

  return (
    <>
      <MyModal
        open={showAddUrl}
        onClose={hideModalHandler}
        backgroundColor="hsl(var(--popover))"
        maxWidth="420px"
        title="Add YouTube links"
      >
        <div>
          <div className="flex flex-col gap-1">
            {rows.map((row, idx) => {
              const label = `YouTube Link # ${idx + 1}`;
              const error = errors[row.key];
              return (
                <AnimatedRow key={row.key} removing={removingIdx === idx} onRemoved={handleRemoved}>
                  <div className="flex flex-col gap-1 py-0.5">
                    <div className="flex flex-row items-center gap-2">
                      <Input
                        placeholder={label}
                        aria-label={label}
                        className="flex-1"
                        value={row.text}
                        onChange={(e) => onRowTextChanged(row.key, e.target.value)}
                        aria-invalid={Boolean(error) || undefined}
                        aria-describedby={error ? `youtube-link-error-${row.key}` : undefined}
                      />
                      <button
                        type="button"
                        aria-label={`Remove ${label}`}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        onClick={() => onRemoveLinkField(idx)}
                        disabled={removingIdx !== null}
                      >
                        <IconCircleMinus className="size-5" />
                      </button>
                    </div>
                    {error && (
                      <p
                        id={`youtube-link-error-${row.key}`}
                        role="alert"
                        className="text-sm text-destructive"
                      >
                        {error}
                      </p>
                    )}
                  </div>
                </AnimatedRow>
              );
            })}
          </div>
          <Button
            variant="default"
            size="sm"
            className="mt-2"
            onClick={onAddNewLinkField}
            disabled={isAddNewLinkDisabled}
          >
            Add New Link
          </Button>
          <Separator className="my-2" />
          <div className="flex flex-row justify-between gap-4">
            <Button onClick={onAddYouTubeLinkClicked}>Add/Update Videos</Button>
            <Button variant="outline" onClick={hideModalHandler}>
              Cancel
            </Button>
          </div>
        </div>
      </MyModal>
      {!readOnly && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Add YouTube Video"
                onClick={showModalHandler}
              >
                <IconBrandYoutube className="size-6" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Add YouTube Video</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </>
  );
};

export default AddYouTube;
